import React, { useEffect, useMemo, useState } from "react";
import { Pencil, Trash2, X } from "lucide-react";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import ConfirmModal from "../../components/ConfirmModal";
import { BACKEND_URL } from "../../config";
import { useAuth } from "../../modules/auth/authContext";

interface Employee {
  id: number;
  name: string;
  type?: string;
  contact?: { phone?: string };
}

interface ShiftTemplate {
  id: number;
  shift_name: string;
  start_time?: string;
  end_time?: string;
  description?: string;
}

interface ShiftRow {
  id: number;
  employee_id: number;
  shift_date: string;
  start_date?: string;
  end_date?: string;
  shift_name?: string;
  start_time?: string;
  end_time?: string;
  employee?: Employee;
}

interface ShiftRecordGroup {
  ids: number[];
  rows: ShiftRow[];
  employee_id: number;
  start_date: string;
  end_date: string;
  shift_name?: string;
  start_time?: string;
  end_time?: string;
  employee?: Employee;
}

const today = () => new Date().toISOString().slice(0, 10);

const normalizeDate = (row: ShiftRow, field: "start" | "end") => {
  if (field === "start") return row.start_date || row.shift_date || "";
  return row.end_date || row.start_date || row.shift_date || "";
};

const addDays = (dateValue: string, days: number) => {
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateValue;
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

const groupConsecutiveShiftRows = (rows: ShiftRow[]): ShiftRecordGroup[] => {
  const sortedRows = [...rows].sort((a, b) => {
    const keyA = `${a.employee_id}|${a.shift_name || ""}|${a.start_time || ""}|${a.end_time || ""}`;
    const keyB = `${b.employee_id}|${b.shift_name || ""}|${b.start_time || ""}|${b.end_time || ""}`;
    return keyA.localeCompare(keyB) || normalizeDate(a, "start").localeCompare(normalizeDate(b, "start"));
  });

  const groups: ShiftRecordGroup[] = [];
  sortedRows.forEach((row) => {
    const rowStart = normalizeDate(row, "start");
    const rowEnd = normalizeDate(row, "end");
    const lastGroup = groups[groups.length - 1];
    const isSameShift =
      lastGroup &&
      lastGroup.employee_id === row.employee_id &&
      (lastGroup.shift_name || "") === (row.shift_name || "") &&
      (lastGroup.start_time || "") === (row.start_time || "") &&
      (lastGroup.end_time || "") === (row.end_time || "");
    const isConsecutive = Boolean(lastGroup && rowStart <= addDays(lastGroup.end_date, 1));

    if (isSameShift && isConsecutive) {
      lastGroup.ids.push(row.id);
      lastGroup.rows.push(row);
      if (rowEnd > lastGroup.end_date) lastGroup.end_date = rowEnd;
      if (rowStart < lastGroup.start_date) lastGroup.start_date = rowStart;
      return;
    }

    groups.push({
      ids: [row.id],
      rows: [row],
      employee_id: row.employee_id,
      start_date: rowStart,
      end_date: rowEnd,
      shift_name: row.shift_name,
      start_time: row.start_time,
      end_time: row.end_time,
      employee: row.employee,
    });
  });

  return groups.sort((a, b) => b.start_date.localeCompare(a.start_date));
};

const EmployeeShifts: React.FC = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [shiftTemplates, setShiftTemplates] = useState<ShiftTemplate[]>([]);
  const [shiftRows, setShiftRows] = useState<ShiftRow[]>([]);
  const [activeTab, setActiveTab] = useState<"templates" | "records">("templates");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, title: "", message: "" });
  const [deleteShift, setDeleteShift] = useState<ShiftTemplate | null>(null);
  const [deleteShiftRecord, setDeleteShiftRecord] = useState<ShiftRecordGroup | null>(null);
  const [editingShiftId, setEditingShiftId] = useState<number | null>(null);
  const [editingShiftRecordId, setEditingShiftRecordId] = useState<number | null>(null);
  const [editingShiftRecordIds, setEditingShiftRecordIds] = useState<number[]>([]);
  const [templateForm, setTemplateForm] = useState({
    shift_name: "",
    start_time: "",
    end_time: "",
    description: "",
  });
  const [shiftForm, setShiftForm] = useState({
    employee_id: "",
    start_date: today(),
    end_date: today(),
    shift_template_id: "",
    shift_name: "",
    start_time: "",
    end_time: "",
  });

  const employeeName = useMemo(() => {
    const map = new Map<number, string>();
    employees.forEach((employee) => map.set(employee.id, employee.name));
    return map;
  }, [employees]);

  const groupedShiftRecords = useMemo(() => groupConsecutiveShiftRows(shiftRows), [shiftRows]);

  const api = async (path: string, options: RequestInit = {}) => {
    const res = await fetch(`${BACKEND_URL}${path}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(options.headers || {}) },
      ...options,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [employeeData, templateData, shiftData] = await Promise.all([
        api("/api/employees"),
        api("/api/attendance/shift-templates"),
        api(`/api/attendance/shifts?date_from=${shiftForm.start_date}&date_to=${shiftForm.end_date}`),
      ]);
      setEmployees(employeeData);
      setShiftTemplates(templateData);
      setShiftRows(shiftData);
    } catch (err: any) {
      setModal({ open: true, title: "Unable to load shifts", message: err.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [shiftForm.start_date]);

  const saveTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateForm.shift_name) {
      setModal({ open: true, title: "Missing shift name", message: "Enter a shift name before saving." });
      return;
    }

    try {
      await api(editingShiftId ? `/api/attendance/shift-templates/${editingShiftId}` : "/api/attendance/shift-templates", {
        method: editingShiftId ? "PUT" : "POST",
        body: JSON.stringify(templateForm),
      });
      setEditingShiftId(null);
      setTemplateForm({ shift_name: "", start_time: "", end_time: "", description: "" });
      setModal({ open: true, title: "Shift saved", message: editingShiftId ? "Shift was updated." : "Shift was added." });
      await loadData();
    } catch (err: any) {
      setModal({ open: true, title: "Unable to save shift", message: err.message || "Please try again." });
    }
  };

  const editShiftTemplate = (shiftTemplate: ShiftTemplate) => {
    setEditingShiftId(shiftTemplate.id);
    setTemplateForm({
      shift_name: shiftTemplate.shift_name || "",
      start_time: shiftTemplate.start_time || "",
      end_time: shiftTemplate.end_time || "",
      description: shiftTemplate.description || "",
    });
  };

  const resetTemplateForm = () => {
    setEditingShiftId(null);
    setTemplateForm({ shift_name: "", start_time: "", end_time: "", description: "" });
  };

  const confirmDeleteShift = async () => {
    if (!deleteShift) return;
    try {
      await api(`/api/attendance/shift-templates/${deleteShift.id}`, { method: "DELETE" });
      setDeleteShift(null);
      if (editingShiftId === deleteShift.id) resetTemplateForm();
      setModal({ open: true, title: "Shift deleted", message: "Shift was removed." });
      await loadData();
    } catch (err: any) {
      setModal({ open: true, title: "Unable to delete shift", message: err.message || "Please try again." });
    }
  };

  const selectShiftTemplate = (shiftTemplateId: string) => {
    const shiftTemplate = shiftTemplates.find((item) => String(item.id) === shiftTemplateId);
    setShiftForm((prev) => ({
      ...prev,
      shift_template_id: shiftTemplateId,
      shift_name: shiftTemplate?.shift_name || "",
      start_time: shiftTemplate?.start_time || "",
      end_time: shiftTemplate?.end_time || "",
    }));
  };

  const saveShiftRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shiftForm.employee_id) {
      setModal({ open: true, title: "Missing employee", message: "Select an employee before saving shift record." });
      return;
    }
    if (!shiftForm.shift_name) {
      setModal({ open: true, title: "Missing shift", message: "Select a shift before saving shift record." });
      return;
    }
    if (!shiftForm.start_date || !shiftForm.end_date || shiftForm.end_date < shiftForm.start_date) {
      setModal({ open: true, title: "Invalid date range", message: "Choose an end date that is on or after the start date." });
      return;
    }

    try {
      await api(editingShiftRecordId ? `/api/attendance/shifts/${editingShiftRecordId}` : "/api/attendance/shifts", {
        method: editingShiftRecordId ? "PUT" : "POST",
        body: JSON.stringify({
          employee_id: Number(shiftForm.employee_id),
          shift_date: shiftForm.start_date,
          start_date: shiftForm.start_date,
          end_date: shiftForm.end_date,
          shift_name: shiftForm.shift_name,
          start_time: shiftForm.start_time,
          end_time: shiftForm.end_time,
          assigned_by_id: user?.employee_id || null,
        }),
      });
      if (editingShiftRecordIds.length > 1) {
        await Promise.all(editingShiftRecordIds.slice(1).map((id) => api(`/api/attendance/shifts/${id}`, { method: "DELETE" })));
      }
      setEditingShiftRecordId(null);
      setEditingShiftRecordIds([]);
      setShiftForm({ employee_id: "", start_date: shiftForm.start_date, end_date: shiftForm.end_date, shift_template_id: "", shift_name: "", start_time: "", end_time: "" });
      setModal({
        open: true,
        title: "Shift record saved",
        message: editingShiftRecordId ? "Employee shift record was updated." : "Employee shift record was saved.",
      });
      await loadData();
    } catch (err: any) {
      setModal({ open: true, title: "Unable to save shift record", message: err.message || "Please try again." });
    }
  };

  const editShiftRecord = (group: ShiftRecordGroup) => {
    const matchingTemplate = shiftTemplates.find((shiftTemplate) =>
      shiftTemplate.shift_name === group.shift_name &&
      (shiftTemplate.start_time || "") === (group.start_time || "") &&
      (shiftTemplate.end_time || "") === (group.end_time || "")
    );
    setEditingShiftRecordId(group.ids[0]);
    setEditingShiftRecordIds(group.ids);
    setActiveTab("records");
    setShiftForm({
      employee_id: String(group.employee_id),
      start_date: group.start_date || today(),
      end_date: group.end_date || group.start_date || today(),
      shift_template_id: matchingTemplate ? String(matchingTemplate.id) : "",
      shift_name: group.shift_name || "",
      start_time: group.start_time || "",
      end_time: group.end_time || "",
    });
  };

  const resetShiftRecordForm = () => {
    setEditingShiftRecordId(null);
    setEditingShiftRecordIds([]);
    setShiftForm({ employee_id: "", start_date: shiftForm.start_date, end_date: shiftForm.end_date, shift_template_id: "", shift_name: "", start_time: "", end_time: "" });
  };

  const confirmDeleteShiftRecord = async () => {
    if (!deleteShiftRecord) return;
    try {
      await Promise.all(deleteShiftRecord.ids.map((id) => api(`/api/attendance/shifts/${id}`, { method: "DELETE" })));
      setDeleteShiftRecord(null);
      if (editingShiftRecordId && deleteShiftRecord.ids.includes(editingShiftRecordId)) resetShiftRecordForm();
      setModal({ open: true, title: "Shift record deleted", message: "Employee shift record was removed." });
      await loadData();
    } catch (err: any) {
      setModal({ open: true, title: "Unable to delete shift record", message: err.message || "Please try again." });
    }
  };

  return (
    <SidebarLayout title="Shifts" subtitle="Add reusable shifts and assign them to employees.">
      <Modal open={modal.open} title={modal.title} message={modal.message} onClose={() => setModal((prev) => ({ ...prev, open: false }))} />
      <ConfirmModal
        open={Boolean(deleteShift)}
        title="Delete shift"
        message={`Delete ${deleteShift?.shift_name || "this shift"}?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteShift}
        onCancel={() => setDeleteShift(null)}
      />
      <ConfirmModal
        open={Boolean(deleteShiftRecord)}
        title="Delete shift record"
        message={`Delete shift record for ${deleteShiftRecord?.employee?.name || employeeName.get(deleteShiftRecord?.employee_id || 0) || "this employee"} from ${deleteShiftRecord?.start_date || "-"} to ${deleteShiftRecord?.end_date || "-"}?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteShiftRecord}
        onCancel={() => setDeleteShiftRecord(null)}
      />

      <section className="rounded border border-border-light bg-surface-light p-5">
        <div className="mb-5 flex flex-wrap gap-2" role="tablist" aria-label="Shift sections">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "templates"}
            onClick={() => setActiveTab("templates")}
            className={`rounded border px-4 py-2 text-sm font-semibold transition ${activeTab === "templates" ? "border-primary bg-primary text-white" : "border-border-light text-subtext-light hover:bg-primary/10 hover:text-primary"}`}
          >
            Shifts
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "records"}
            onClick={() => setActiveTab("records")}
            className={`rounded border px-4 py-2 text-sm font-semibold transition ${activeTab === "records" ? "border-primary bg-primary text-white" : "border-border-light text-subtext-light hover:bg-primary/10 hover:text-primary"}`}
          >
            Shift Records
          </button>
        </div>

        {activeTab === "templates" && (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(320px,420px)_1fr]">
            <form onSubmit={saveTemplate} className="rounded border border-border-light bg-background-light p-4">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold">{editingShiftId ? "Edit Shift" : "Add Shift"}</h2>
                {editingShiftId && (
                  <button type="button" onClick={resetTemplateForm} title="Cancel edit" className="rounded border border-border-light p-2 text-subtext-light hover:bg-primary/10 hover:text-primary">
                    <X size={18} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3">
                <label>
                  <div className="mb-1 text-sm font-semibold">Shift Name</div>
                  <input required value={templateForm.shift_name} onChange={(e) => setTemplateForm((prev) => ({ ...prev, shift_name: e.target.value }))} className="w-full rounded border p-3" />
                </label>
                <label>
                  <div className="mb-1 text-sm font-semibold">Start Time</div>
                  <input type="time" value={templateForm.start_time} onChange={(e) => setTemplateForm((prev) => ({ ...prev, start_time: e.target.value }))} className="w-full rounded border p-3" />
                </label>
                <label>
                  <div className="mb-1 text-sm font-semibold">End Time</div>
                  <input type="time" value={templateForm.end_time} onChange={(e) => setTemplateForm((prev) => ({ ...prev, end_time: e.target.value }))} className="w-full rounded border p-3" />
                </label>
                <label>
                  <div className="mb-1 text-sm font-semibold">Description</div>
                  <input value={templateForm.description} onChange={(e) => setTemplateForm((prev) => ({ ...prev, description: e.target.value }))} className="w-full rounded border p-3" />
                </label>
              </div>
              <div className="mt-4 flex justify-end">
                <button type="submit" className="rounded bg-primary px-5 py-2 font-semibold text-white">{editingShiftId ? "Update Shift" : "Add Shift"}</button>
              </div>
            </form>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead className="bg-background-light text-xs uppercase text-subtext-light">
                  <tr>
                    <th className="p-2">Shift</th>
                    <th className="p-2">Start</th>
                    <th className="p-2">End</th>
                    <th className="p-2">Description</th>
                    <th className="p-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shiftTemplates.map((shiftTemplate) => (
                    <tr key={shiftTemplate.id} className="border-t">
                      <td className="p-2 font-semibold">{shiftTemplate.shift_name}</td>
                      <td className="p-2">{shiftTemplate.start_time || "-"}</td>
                      <td className="p-2">{shiftTemplate.end_time || "-"}</td>
                      <td className="p-2">{shiftTemplate.description || "-"}</td>
                      <td className="p-2">
                        <div className="flex justify-end gap-2">
                          <button type="button" onClick={() => editShiftTemplate(shiftTemplate)} title="Edit shift" className="rounded p-2 text-primary hover:bg-primary/10">
                            <Pencil size={16} />
                          </button>
                          <button type="button" onClick={() => setDeleteShift(shiftTemplate)} title="Delete shift" className="rounded p-2 text-alert-red hover:bg-alert-red/10">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!shiftTemplates.length && <tr><td colSpan={5} className="p-4 text-center text-subtext-light">No shifts added yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "records" && (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(320px,420px)_1fr]">
            <form onSubmit={saveShiftRecord} className="rounded border border-border-light bg-background-light p-4">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold">{editingShiftRecordId ? "Edit Shift Record" : "Add Shift Record"}</h2>
                {editingShiftRecordId && (
                  <button type="button" onClick={resetShiftRecordForm} title="Cancel edit" className="rounded border border-border-light p-2 text-subtext-light hover:bg-primary/10 hover:text-primary">
                    <X size={18} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3">
                <label>
                  <div className="mb-1 text-sm font-semibold">Employee</div>
                  <select required value={shiftForm.employee_id} onChange={(e) => setShiftForm((prev) => ({ ...prev, employee_id: e.target.value }))} className="w-full rounded border p-3">
                    <option value="">Select employee</option>
                    {employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
                  </select>
                </label>
                <label>
                  <div className="mb-1 text-sm font-semibold">Start Date</div>
                  <input
                    type="date"
                    value={shiftForm.start_date}
                    onChange={(e) => setShiftForm((prev) => ({ ...prev, start_date: e.target.value, end_date: prev.end_date < e.target.value ? e.target.value : prev.end_date }))}
                    className="w-full rounded border p-3"
                  />
                </label>
                <label>
                  <div className="mb-1 text-sm font-semibold">End Date</div>
                  <input
                    type="date"
                    min={shiftForm.start_date}
                    value={shiftForm.end_date}
                    onChange={(e) => setShiftForm((prev) => ({ ...prev, end_date: e.target.value }))}
                    className="w-full rounded border p-3"
                  />
                </label>
                <label>
                  <div className="mb-1 text-sm font-semibold">Shift</div>
                  <select required value={shiftForm.shift_template_id} onChange={(e) => selectShiftTemplate(e.target.value)} className="w-full rounded border p-3">
                    <option value="">Select shift</option>
                    {shiftTemplates.map((shiftTemplate) => (
                      <option key={shiftTemplate.id} value={shiftTemplate.id}>
                        {shiftTemplate.shift_name} ({shiftTemplate.start_time || "-"} - {shiftTemplate.end_time || "-"})
                      </option>
                    ))}
                  </select>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label>
                    <div className="mb-1 text-sm font-semibold">Start</div>
                    <input type="time" value={shiftForm.start_time} onChange={(e) => setShiftForm((prev) => ({ ...prev, start_time: e.target.value }))} className="w-full rounded border p-3" />
                  </label>
                  <label>
                    <div className="mb-1 text-sm font-semibold">End</div>
                    <input type="time" value={shiftForm.end_time} onChange={(e) => setShiftForm((prev) => ({ ...prev, end_time: e.target.value }))} className="w-full rounded border p-3" />
                  </label>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button type="submit" className="rounded bg-primary px-5 py-2 font-semibold text-white">{editingShiftRecordId ? "Update Shift Record" : "Save Shift Record"}</button>
              </div>
            </form>

            {loading ? <div>Loading...</div> : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-left text-sm">
                  <thead className="bg-background-light text-xs uppercase text-subtext-light">
                    <tr>
                      <th className="p-2">Employee</th>
                      <th className="p-2">Start Date</th>
                      <th className="p-2">End Date</th>
                      <th className="p-2">Shift</th>
                      <th className="p-2">Start</th>
                      <th className="p-2">End</th>
                      <th className="p-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedShiftRecords.map((group) => (
                      <tr key={group.ids.join("-")} className="border-t">
                        <td className="p-2 font-semibold">{group.employee?.name || employeeName.get(group.employee_id) || group.employee_id}</td>
                        <td className="p-2">{group.start_date || "-"}</td>
                        <td className="p-2">{group.end_date || group.start_date || "-"}</td>
                        <td className="p-2">{group.shift_name || "-"}</td>
                        <td className="p-2">{group.start_time || "-"}</td>
                        <td className="p-2">{group.end_time || "-"}</td>
                        <td className="p-2">
                          <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => editShiftRecord(group)} title="Edit shift record" className="rounded p-2 text-primary hover:bg-primary/10">
                              <Pencil size={16} />
                            </button>
                            <button type="button" onClick={() => setDeleteShiftRecord(group)} title="Delete shift record" className="rounded p-2 text-alert-red hover:bg-alert-red/10">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!groupedShiftRecords.length && <tr><td colSpan={7} className="p-4 text-center text-subtext-light">No shift records for this date range.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>
    </SidebarLayout>
  );
};

export default EmployeeShifts;
